import React, { useState } from 'react';
import { Search, Banknote, CheckCircle2, TrendingUp, Loader2, CalendarRange, Info, LayoutList, History } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useClients, useRecordPayment, useDashboardStats, useEndDaySession } from '@/hooks/use-api';
import { useRoleStore } from '@/lib/store';
import { Skeleton } from '@/components/ui/skeleton';
import type { SusuClient } from '@shared/types';
export function DailyCollectionsPage() {
  const [search, setSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<SusuClient | null>(null);
  const [amount, setAmount] = useState('');
  const [isEndDayOpen, setIsEndDayOpen] = useState(false);
  const [cashCount, setCashCount] = useState('');
  const [filterType, setFilterType] = useState('all');
  const { data: clientsData, isLoading: clientsLoading } = useClients({
    atRisk: filterType === 'missed'
  });
  const { data: statsData } = useDashboardStats();
  const recordPayment = useRecordPayment();
  const endDay = useEndDaySession();
  const role = useRoleStore(s => s.role);
  const filtered = (clientsData?.items || []).filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search)
  );
  const systemTotal = statsData?.totalCollections ?? 0;
  const handleConfirm = async () => {
    if (!selectedClient || !amount || parseFloat(amount) <= 0) return;
    try {
      await recordPayment.mutateAsync({
        clientId: selectedClient.id,
        amount: parseFloat(amount),
        type: 'Collection',
        officerId: 'u1',
        actorName: role
      });
      toast.success(`GHS ${amount} collected from ${selectedClient.name}`);
      setAmount('');
      setSelectedClient(null);
    } catch (error) {
      toast.error("Role mismatch or connection error.");
    }
  };
  const handleEndDay = async () => {
    const countNum = parseFloat(cashCount);
    const diff = countNum - systemTotal;
    try {
      await endDay.mutateAsync({
        officerId: 'u1',
        systemTotal,
        cashCount: countNum,
        difference: diff,
        notes: diff === 0
          ? "Perfect daily reconciliation"
          : `Reconciliation discrepancy: GHS ${diff.toFixed(2)}`,
        actorName: role
      });
      toast.success("Daily reconciliation submitted successfully");
      setIsEndDayOpen(false);
      setCashCount('');
    } catch (e) {
      toast.error("Failed to reconcile session");
    }
  };
  return (
    <AppLayout container className="bg-accent/20 scrollbar-custom">
      <div className="max-w-7xl mx-auto space-y-8 animate-fade-in">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Field Collections</h1>
            <p className="text-muted-foreground mt-1 font-medium">Rapid entry for daily Susu route management.</p>
          </div>
          <Button
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12 rounded-xl shadow-lg shadow-primary/20 font-bold"
            onClick={() => setIsEndDayOpen(true)}
          >
            <History className="h-4 w-4 mr-2" />
            End Day Reconciliation
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-primary p-6 rounded-2xl text-primary-foreground shadow-lg col-span-1 md:col-span-2">
            <p className="text-primary-foreground/80 text-[10px] font-bold uppercase tracking-widest mb-2">Portfolio Volume</p>
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-4xl font-black">GHS {systemTotal.toLocaleString()}</h2>
                <p className="text-primary-foreground/80 text-xs font-bold mt-1 uppercase tracking-tight">Total collections tracked live</p>
              </div>
              <TrendingUp className="h-12 w-12 opacity-30" />
            </div>
          </div>
          <Card className="flex flex-col justify-center p-6 border-none shadow-soft bg-card rounded-2xl">
            <p className="text-muted-foreground text-[10px] font-bold uppercase tracking-widest mb-1">Status Report</p>
            <h3 className="text-2xl font-black text-foreground">Active Route</h3>
          </Card>
          <Card className="flex flex-col justify-center p-6 border-none shadow-soft bg-card rounded-2xl">
            <p className="text-muted-foreground text-[10px] font-bold uppercase tracking-widest mb-1">Region</p>
            <h3 className="text-2xl font-black text-foreground">Operational</h3>
          </Card>
        </div>
        <div className="flex flex-col space-y-4">
          <Tabs value={filterType} onValueChange={setFilterType} className="w-full">
            <TabsList className="bg-muted/50 p-1 rounded-xl border border-border">
              <TabsTrigger value="all" className="flex items-center gap-2 px-6 rounded-lg data-[state=active]:bg-card data-[state=active]:text-foreground font-bold">
                <LayoutList className="h-4 w-4" /> All Route
              </TabsTrigger>
              <TabsTrigger value="missed" className="flex items-center gap-2 px-6 rounded-lg data-[state=active]:bg-card data-[state=active]:text-destructive font-bold">
                <Info className="h-4 w-4" /> Priority Follow-up
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search client name or phone number..."
              className="pl-12 h-16 text-lg rounded-2xl border-border shadow-soft focus-visible:ring-primary bg-card font-medium text-foreground"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 scrollbar-custom">
          <AnimatePresence mode="popLayout">
            {!selectedClient ? (
              clientsLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-24 w-full rounded-2xl bg-muted/50" />
                ))
              ) : (
                filtered.map(client => (
                  <motion.div
                    key={client.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                  >
                    <Card
                      className="cursor-pointer hover:bg-card hover:shadow-glow-lg active:scale-[0.98] transition-all border border-border hover:border-border bg-card/80 shadow-soft rounded-2xl"
                      onClick={() => setSelectedClient(client)}
                    >
                      <CardContent className="p-5 flex items-center justify-between">
                        <div className="space-y-1">
                          <p className="font-bold text-foreground">{client.name}</p>
                          <p className="text-xs text-muted-foreground font-bold flex items-center gap-1 uppercase">
                            <CalendarRange className="h-3 w-3" />
                            {client.lastPayment ? new Date(client.lastPayment).toLocaleDateString() : 'New Client'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-black text-primary">GHS {client.balance.toFixed(0)}</p>
                          <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-tight">{client.territory}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              )
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="col-span-full max-w-xl mx-auto w-full"
              >
                <div className="bg-card border border-border rounded-3xl p-8 shadow-glass space-y-8">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-16 rounded-2xl bg-muted flex items-center justify-center text-primary font-black text-2xl border border-border">
                        {selectedClient.name[0]}
                      </div>
                      <div>
                        <h3 className="text-2xl font-black text-foreground tracking-tight">{selectedClient.name}</h3>
                        <p className="text-muted-foreground font-bold uppercase text-xs tracking-widest">{selectedClient.phone} • {selectedClient.territory}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="rounded-full h-10 w-10 p-0 text-muted-foreground" onClick={() => setSelectedClient(null)}>✕</Button>
                  </div>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest px-1">Payment Amount (GHS)</label>
                      <Input
                        type="number"
                        inputMode="decimal"
                        className="h-20 text-4xl font-mono font-black text-center border-none bg-muted/50 rounded-2xl focus-visible:ring-primary text-foreground"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[10, 20, 50, 100, 200, 500].map(val => (
                        <Button
                          key={val}
                          variant="outline"
                          className="h-14 font-black rounded-xl border-border text-muted-foreground hover:bg-muted/50 transition-colors"
                          onClick={() => setAmount(val.toString())}
                        >
                          {val}
                        </Button>
                      ))}
                    </div>
                    <Button
                      className="w-full h-20 text-xl font-black bg-primary hover:bg-primary/90 rounded-2xl shadow-lg shadow-primary/20 text-primary-foreground"
                      onClick={handleConfirm}
                      disabled={!amount || recordPayment.isPending}
                    >
                      {recordPayment.isPending ? (
                        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-2 h-6 w-6" />
                      )}
                      Record Payment
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <Dialog open={isEndDayOpen} onOpenChange={setIsEndDayOpen}>
          <DialogContent className="sm:max-w-md rounded-2xl border-border">
            <DialogHeader>
              <DialogTitle className="text-foreground font-black">Daily Cash Reconciliation</DialogTitle>
              <DialogDescription className="text-muted-foreground font-medium">Verify physical cash against system recorded collections.</DialogDescription>
            </DialogHeader>
            <div className="py-6 space-y-6">
              <div className="bg-muted/50 p-4 rounded-xl border border-border flex justify-between items-center">
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">System Total</span>
                <span className="text-xl font-black text-foreground">GHS {systemTotal.toLocaleString()}</span>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-foreground uppercase tracking-widest">Physical Cash Count</label>
                <Input
                  type="number"
                  placeholder="Enter counted amount"
                  className="h-12 rounded-xl border-border bg-card focus-visible:ring-primary font-bold text-foreground"
                  value={cashCount}
                  onChange={(e) => setCashCount(e.target.value)}
                />
              </div>
              {cashCount && (
                <div className={`p-4 rounded-xl flex items-center gap-3 ${parseFloat(cashCount) === systemTotal ? 'bg-emerald-500/10 text-emerald-600' : 'bg-destructive/10 text-destructive'}`}>
                  <Info className="h-5 w-5" />
                  <p className="text-sm font-bold uppercase tracking-tight">
                    {parseFloat(cashCount) === systemTotal ? 'Cash matches perfectly!' : `Discrepancy: GHS ${(parseFloat(cashCount) - systemTotal).toFixed(2)}`}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setIsEndDayOpen(false)} className="rounded-xl font-bold text-muted-foreground hover:bg-muted/50">Cancel</Button>
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold px-6"
                onClick={handleEndDay}
                disabled={!cashCount || endDay.isPending}
              >
                {endDay.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Submit & Close Day
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}